import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Globe, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrayerWall = () => {
  const [testimonies, setTestimonies] = useState([]);
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchTestimonies();
    const interval = setInterval(fetchTestimonies, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchTestimonies = async () => {
    try {
      const { data } = await axios.get(`${API}/testimonies`);
      setTestimonies(data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/testimonies`, { name, location, message });
      toast.success("Testimony posted to the Prayer Wall!");
      setName("");
      setLocation("");
      setMessage("");
      fetchTestimonies();
    } catch (err) {
      toast.error("Failed to post. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const tickerText = testimonies.length > 0
    ? testimonies.map(t => `${t.name}${t.location ? ` (${t.location})` : ""}: "${t.message}"`).join("   ///   ")
    : "Welcome to the Global Prayer Wall — Share your testimony with the world...";

  return (
    <section id="prayer-wall" className="section-spacing bg-white" data-testid="prayer-wall-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-overline mb-4" data-testid="prayer-wall-overline">Global Prayer Wall</p>
          <h2 className="heading-massive text-4xl sm:text-5xl text-black mb-4" data-testid="prayer-wall-heading">
            Testimonies
          </h2>
          <p className="font-['Satoshi'] text-lg text-zinc-600 max-w-2xl mx-auto">
            Share your testimony with the world. Watch God's faithfulness broadcast live from believers worldwide.
          </p>
        </div>

        {/* Ticker */}
        <div className="ticker-wrap mb-12" data-testid="testimony-ticker">
          <div className="ticker">
            <span>{tickerText}</span>
            <span>{tickerText}</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Form */}
          <div className="card-hard-shadow bg-zinc-50 p-8" data-testid="testimony-form-container">
            <div className="flex items-center gap-3 mb-6">
              <Globe size={24} className="text-[#FFB800]" />
              <h3 className="font-['Clash_Display'] font-semibold text-xl">Post to the Wall</h3>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="testimony-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-['Satoshi'] font-medium text-sm mb-2 block">Your Name *</label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John D."
                    required
                    className="input-sharp h-11"
                    data-testid="testimony-name"
                  />
                </div>
                <div>
                  <label className="font-['Satoshi'] font-medium text-sm mb-2 block">Location</label>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="New Orleans, LA"
                    className="input-sharp h-11"
                    data-testid="testimony-location"
                  />
                </div>
              </div>
              <div>
                <label className="font-['Satoshi'] font-medium text-sm mb-2 block">Your Testimony *</label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Share what God has done in your life..."
                  required
                  className="input-sharp min-h-[100px]"
                  data-testid="testimony-message"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-sharp bg-black text-white hover:bg-white hover:text-black hover:shadow-[inset_0_0_0_2px_black] w-full py-5 text-sm flex items-center justify-center gap-2"
                data-testid="testimony-submit"
              >
                {isSubmitting ? "Posting..." : <><Send size={16} /> Post to Wall</>}
              </Button>
            </form>
          </div>

          {/* Live Feed */}
          <div data-testid="testimony-feed">
            <h3 className="font-['Clash_Display'] font-semibold text-xl mb-6">Live Feed</h3>
            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
              {testimonies.slice(0, 10).map((t) => (
                <div key={t.id} className="card-hard-shadow bg-white p-5" data-testid={`testimony-${t.id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="font-['Clash_Display'] font-semibold text-base">{t.name}</span>
                      {t.location && (
                        <span className="font-['Satoshi'] text-xs text-zinc-400 ml-2">{t.location}</span>
                      )}
                    </div>
                    <span className="font-['Satoshi'] text-xs text-zinc-400">
                      {new Date(t.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="font-['Satoshi'] text-zinc-700 leading-relaxed">"{t.message}"</p>
                </div>
              ))}
              {testimonies.length === 0 && (
                <div className="text-center py-12">
                  <p className="font-['Satoshi'] text-zinc-400">No testimonies yet. Be the first to share!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PrayerWall;
